if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.onViewportResize = null;
        this.onHeaderFreezeResize = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.unwatchViewportHeight();
        this.unfreezeHeaderHeight();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.freezeHeaderHeight();
        this.watchViewportHeight();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      /* MAHA 2026-09-01 -- FICHA-GALERIA-CAJA-CONGELADA (causa 2 del informe
         galeria-barrido-alturas).

         QUE PASA: Swiper corre con autoHeight:true (setSliderOptions, mas
         arriba) y por tanto escribe un `height` INLINE en .swiper-wrapper
         (.product__media-list). Ese valor inline es el que fija el alto de
         .product__media-container -- la caja que .sticky-element pega. El
         max-height que la ficha aplica a .product__media-item-container vive
         DENTRO de ese wrapper, asi que encoge la foto y deja la caja igual:
         medido a 1440 (bloque activo, ventana 900 -> 600), item pasa de 629.2
         a 562 mientras la caja se queda en 631.

         POR QUE SOLO EN EL REDIMENSIONADO, Y NO EN LA CARGA NI AL CAMBIAR DE
         FOTO: esos dos momentos ya son correctos hoy, verificados uno por uno
         -- en la carga el CSS se aplica antes de que Swiper mida, y al cambiar
         de foto el propio autoHeight reescribe el inline (con el video de
         botella-vidrio la caja pasa de 813 a 964, sola). El unico momento roto
         es el cambio de ALTO de ventana, por dos motivos que se suman:
           1. FoxTheme.Carousel (theme.js:356) solo carga los modulos
              Navigation, Pagination, A11y y Thumbs. El modulo Resize de Swiper
              NO esta, asi que params.updateOnWindowResize:true es inerte: no
              hay nadie escuchando el resize de ventana.
           2. El resizeObserver del core observa la CAJA del carrusel, cuyo
              alto esta congelado por ese mismo height inline. Un cambio de alto
              de ventana no altera la caja, asi que el observer no dispara
              nunca. Un cambio de ANCHO si la altera, y por eso ese caso ya
              funciona hoy sin tocar nada (verificado: 1920 -> 1200 se recalcula
              solo, caja 490 e item 487.8 quedan de acuerdo).

         POR QUE updateAutoHeight(0) Y NO update(): medido sobre 30 muestras en
         esta misma ficha, updateAutoHeight = 0 ms de mediana (p90 0.1, max 0.1)
         contra 30.9 ms de mediana de update() (p90 39.3). update() ademas
         recalcula anchos y posiciones de slide, que en un cambio de alto no
         cambian, y el ancho ya lo cubre el resizeObserver del punto 2.

         LO QUE NO TOCA: autoHeight sigue en true (apagarlo haria que la caja
         tomara el alto del slide MAS ALTO; en botella-vidrio el video mide
         797.7 contra 629.2 de las fotos, y la caja se iria a 799.7 con la fila
         en 800.4 -- la galeria dejaria de pegarse a 1440x900, justo donde hoy
         funciona). Tampoco toca el piso de min-height:680px de pdp-maha.css,
         ni theme.css, ni la formula del top. */
      watchViewportHeight() {
        this.unwatchViewportHeight();

        let altoPrevio = window.innerHeight;
        let pendiente = false;

        this.onViewportResize = () => {
          // Solo el ALTO: el ancho ya lo resuelve el resizeObserver del core.
          if (window.innerHeight === altoPrevio || pendiente) return;
          pendiente = true;
          requestAnimationFrame(() => {
            pendiente = false;
            altoPrevio = window.innerHeight;
            try {
              const slider = this.sliderInstance && this.sliderInstance.slider;
              if (!slider || slider.destroyed || !slider.params.autoHeight) return;
              slider.updateAutoHeight(0);
            } catch (e) {
              // Un fallo aqui no puede tumbar el resto de listeners de resize.
            }
          });
        };

        window.addEventListener('resize', this.onViewportResize);
      }

      unwatchViewportHeight() {
        if (this.onViewportResize) {
          window.removeEventListener('resize', this.onViewportResize);
          this.onViewportResize = null;
        }
      }

      /* MAHA 2026-09-01 -- FICHA-GALERIA-PLAN-B: congelar la altura de cabecera.

         PARA QUE: el max-height de la galeria (pdp-maha.css, bloque
         FICHA-GALERIA-PEGAJOSA) dependia de --header-height, que pasa de 139px
         expandida a 62px colapsada AL HACER SCROLL. Cada cambio encogia la foto,
         la caja de Swiper no la seguia, y quedaba hueco: 108px a 1440x700, 141px
         a 1920x768. Ahora ese max-height mira --pdp-header-height-frozen, que
         publica este metodo y que NO se mueve durante el scroll.

         POR QUE NO SE ARREGLA HACIENDO QUE LA CAJA SIGA A LA FOTO: se intento
         dos veces y se revirtio dos veces. Medido: el coste no viene de LEER el
         layout, viene de que la caja CAMBIE DE TAMANO mientras se hace scroll --
         Swiper emite un evento, el manejador llama a slideTo y slideTo llama a su
         propio updateAutoHeight, que lee offsetHeight y fuerza el reflujo (main:
         0 escrituras / 0 slideTo / 0 ms; con aquel parche: 5 / 5 / 63.8 ms, y la
         mitad de los cuadros a CPU 4x). Si el alto no cambia, no hay nada que
         pagar.

         CUANDO SE CAPTURA:
           - al cargar, sin condiciones. Verificado en 1024, 1440 y 1920: al
             cargar --header-height y .header-section.offsetHeight valen ya la
             altura EXPANDIDA, incluso recargando con la pagina desplazada.
           - en resize de ventana, y SOLO si la cabecera esta expandida. Con
             is-hide-nav mide 62px de verdad, y congelar 62 dejaria la foto
             demasiado alta: volveria el hueco. Si el usuario redimensiona con la
             pagina bajada, se conserva el valor anterior.
           - NUNCA durante el scroll. Ese es el punto entero de este bloque.

         POR QUE SE MIDE EL ELEMENTO Y NO LA VARIABLE: en un resize, header.js
         reescribe --header-height desde su ResizeObserver, que corre DESPUES de
         los listeners de resize; leer la variable ahi daria el valor viejo.
         .header-section.offsetHeight da el valor nuevo en el momento. Verificado
         que ambos coinciden en reposo: 139px a 1440 y 1920, 171px a 1024.

         UN SOLO NUMERO NO EXISTE, POR ESO SE CAPTURA: la cabecera mide 139px de
         1280 para arriba pero 171px a 1024. Cualquier literal habria estado 32px
         corto justo en el ancho mas estrecho del tramo de escritorio. */
      freezeHeaderHeight() {
        this.unfreezeHeaderHeight();

        const capturar = (exigirExpandida) => {
          try {
            if (exigirExpandida && document.body.classList.contains('is-hide-nav')) return;
            const cabecera = document.querySelector('.header-section');
            if (!cabecera) return;
            const alto = cabecera.offsetHeight;
            if (!(alto > 0)) return;
            const px = alto + 'px';
            const raiz = document.documentElement;
            if (raiz.style.getPropertyValue('--pdp-header-height-frozen') === px) return;
            raiz.style.setProperty('--pdp-header-height-frozen', px);
          } catch (e) {
            // Un fallo aqui deja el respaldo var(--header-height) de la hoja de
            // estilos, que es el comportamiento de antes de este bloque.
          }
        };

        capturar(false);

        let pendiente = false;
        this.onHeaderFreezeResize = () => {
          if (pendiente) return;
          pendiente = true;
          requestAnimationFrame(() => {
            pendiente = false;
            capturar(true);
          });
        };
        window.addEventListener('resize', this.onHeaderFreezeResize);
      }

      unfreezeHeaderHeight() {
        if (this.onHeaderFreezeResize) {
          window.removeEventListener('resize', this.onHeaderFreezeResize);
          this.onHeaderFreezeResize = null;
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, realIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            // realIndex (no activeIndex): el slider principal usa loop:true
            // y el de thumbnails no -- activeIndex incluye los slides
            // clonados por el loop, desfasando la miniatura activa respecto
            // al slide real que se está mostrando.
            thumbs.swiper.slideTo(realIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
